import React, { useState, useEffect } from 'react';
import { X, Loader2, Check, MapPin, Users, PenLine } from 'lucide-react';
import { InstructionSignModal } from './InstructionSignModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const ResendInstructionModal = ({ instruction, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendMode, setSendMode] = useState('selected');
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [includeConfirmed, setIncludeConfirmed] = useState(false);
  const [confirmations, setConfirmations] = useState([]);

  // SL-Unterschrift: true = nach Senden unterschreiben lassen
  const [showSignModal, setShowSignModal] = useState(false);
  const [slName, setSlName] = useState('');

  useEffect(() => {
    fetchData();
    // Aktuellen Admin/SL-Namen aus JWT laden
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const p = JSON.parse(atob(token.split('.')[1]));
        setSlName(p.name || p.sub || '');
      } catch { /* silent */ }
    }
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [locationsRes, employeesRes, confirmationsRes] = await Promise.all([
        axios.get(`${API}/api/admin/locations`, { headers }),
        axios.get(`${API}/api/admin/employees`, { headers }),
        axios.get(`${API}/api/instructions/tracking`, { headers })
      ]);
      
      setLocations(locationsRes.data || []);
      setEmployees(employeesRes.data || []);
      
      // Find confirmations for this instruction
      const tracking = confirmationsRes.data || [];
      const instructionTracking = tracking.find(t => t.instruction_id === instruction.id);
      setConfirmations(instructionTracking?.confirmations || []);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationToggle = (locationId) => {
    setSelectedLocations(prev => 
      prev.includes(locationId) ? prev.filter(id => id !== locationId) : [...prev, locationId]
    );
  };

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSelectAllLocations = () => {
    if (selectedLocations.length === locations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations(locations.map(l => l.id));
    }
  };

  const handleSelectAllEmployees = () => {
    const filteredEmployees = getFilteredEmployees();
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map(e => e.id));
    }
  };

  const getFilteredEmployees = () => {
    let filtered = employees;
    
    // Filter by selected locations
    if (selectedLocations.length > 0) {
      filtered = filtered.filter(emp => 
        emp.location_ids?.some(locId => selectedLocations.includes(locId))
      );
    }
    
    // Filter out confirmed if not includeConfirmed
    if (!includeConfirmed) {
      const confirmedIds = confirmations
        .filter(c => c.confirmed_at)
        .map(c => c.user_id);
      filtered = filtered.filter(emp => !confirmedIds.includes(emp.id));
    }
    
    return filtered;
  };

  const handleResend = async () => {
    if (sendMode === 'selected' && selectedEmployees.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Mitarbeiter aus');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      let response;

      if (sendMode === 'all') {
        response = await axios.post(
          `${API}/api/admin/instructions/${instruction.id}/send-to-all`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${API}/api/admin/instructions/${instruction.id}/send-to-selected`,
          selectedEmployees,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      }

      toast.success(response.data.message || 'Unterweisung erfolgreich versendet');
      // Nach dem Senden: SL-Unterschrift anbieten
      setShowSignModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Versenden');
      setSending(false);
    } finally {
      setSending(false);
    }
  };

  const handleSlSign = async (signatureBase64) => {
    const token = localStorage.getItem('token');
    await axios.put(
      `${API}/api/instructions/${instruction.id}/sign-sl`,
      { signature_sl: signatureBase64 },
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    toast.success('Unterweisung unterschrieben und versendet');
    onSuccess();
    onClose();
  };

  const handleSkipSign = () => {
    onSuccess();
    onClose();
  };

  const filteredEmployees = getFilteredEmployees();

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Unterweisung erneut versenden</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">{instruction.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          </div>
        ) : (
          <div className="p-4 sm:p-6 space-y-6">
            {/* Send Mode Selection */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSendMode('all')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendMode === 'all'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Users className="h-5 w-5 mx-auto mb-2" />
                <p className="text-sm font-medium">An alle senden</p>
                <p className="text-xs text-gray-500 mt-1">Alle Mitarbeiter</p>
              </button>
              <button
                onClick={() => setSendMode('selected')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendMode === 'selected'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Check className="h-5 w-5 mx-auto mb-2" />
                <p className="text-sm font-medium">Auswahl</p>
                <p className="text-xs text-gray-500 mt-1">Gezielt versenden</p>
              </button>
            </div>

            {sendMode === 'selected' && (
              <>
                {/* Include Confirmed Option */}
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="include_confirmed"
                    checked={includeConfirmed}
                    onChange={(e) => setIncludeConfirmed(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <label htmlFor="include_confirmed" className="text-sm text-gray-700">
                    Auch Mitarbeiter einschließen, die bereits bestätigt haben
                  </label>
                </div>

                {/* Locations */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Standorte auswählen
                    </label>
                    <button
                      onClick={handleSelectAllLocations}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {selectedLocations.length === locations.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                    {locations.map((location) => (
                      <label
                        key={location.id}
                        className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{location.name}</p>
                          <p className="text-xs text-gray-500 truncate">{location.city}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Employees */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Mitarbeiter auswählen
                      <span className="text-xs text-gray-500">({filteredEmployees.length} verfügbar)</span>
                    </label>
                    <button
                      onClick={handleSelectAllEmployees}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {selectedEmployees.length === filteredEmployees.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                  </div>
                  
                  {filteredEmployees.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 bg-gray-50 rounded-lg">
                      {includeConfirmed 
                        ? 'Keine Mitarbeiter gefunden'
                        : 'Alle Mitarbeiter haben diese Unterweisung bereits bestätigt'}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                      {filteredEmployees.map((employee) => {
                        const isConfirmed = confirmations.find(c => c.user_id === employee.id && c.confirmed_at);
                        return (
                          <label
                            key={employee.id}
                            className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedEmployees.includes(employee.id)}
                              onChange={() => handleEmployeeToggle(employee.id)}
                              className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 truncate">{employee.name}</p>
                                {isConfirmed && <Check className="h-3 w-3 text-green-600" />}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{employee.email}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {sendMode === 'all' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>📢 Hinweis:</strong> Diese Unterweisung wird an alle Mitarbeiter Ihres Unternehmens versendet ({employees.length} Mitarbeiter).
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleResend}
                disabled={sending || (sendMode === 'selected' && selectedEmployees.length === 0)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Wird versendet...
                  </>
                ) : (
                  <>
                    {sendMode === 'all' 
                      ? `An alle versenden (${employees.length})`
                      : `Jetzt versenden (${selectedEmployees.length})`
                    }
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* SL-Unterschrift nach dem Versenden */}
    <InstructionSignModal
      open={showSignModal}
      onClose={handleSkipSign}
      onSign={handleSlSign}
      title={instruction?.title}
      signerLabel="Stationsleitung / Admin"
      signerName={slName}
      confirmLabel="Jetzt unterschreiben"
      isEmployee={false}
    />
    </>
  );
};
